import { useRef, useState } from 'react';
import { dec, formatMoney } from '@makila/fiscal';
import { Camera, Plus, UserCog } from 'lucide-react';
import clsx from 'clsx';
import { patch, post } from './api';
import { useBusiness } from './business';
import { Alert, Badge, Button, Card, Dialog, EmptyState, Field, FormActions, Input, NumberInput, PhoneInput, Section, Select, Skeleton, Toggle, useToast } from './ui';
import { attempt, useLoad } from './useLoad';

interface StaffService { product_id: string; price: string | null; duration_min: number | null }
interface Slot { day: number; from: string; to: string }
interface Operator {
  id: string; name: string; role: 'cashier' | 'manager'; active: boolean;
  performs_services: boolean; color: string | null; photo: string | null; phone: string | null; commission_service_pct: string; commission_product_pct: string;
  chair_rental: boolean; schedule: Slot[] | null; sort_order: number; services: StaffService[];
}
interface Product { id: string; name: string; kind: string; unit_price: string; duration_min: number | null; active: boolean }
const ROLE = { cashier: 'Caixa', manager: 'Gerente' } as const;
const DAYS = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'] as const;
const PALETTE = ['#1d4ed8', '#be185d', '#0f766e', '#b45309', '#7c3aed', '#dc2626', '#0369a1', '#4d7c0f'];

/** Fotografia do profissional: quadrada, 160 px (vai às caixas, para a fila e a agenda). */
async function photoThumb(file: File): Promise<string> {
  const bmp = await createImageBitmap(file);
  const side = Math.min(bmp.width, bmp.height);
  const c = document.createElement('canvas');
  c.width = c.height = 160;
  c.getContext('2d')!.drawImage(bmp, (bmp.width - side) / 2, (bmp.height - side) / 2, side, side, 0, 0, 160, 160);
  for (const q of [0.8, 0.6, 0.45]) {
    let u = c.toDataURL('image/webp', q);
    if (!u.startsWith('data:image/webp')) u = c.toDataURL('image/jpeg', q);
    if (u.length < 38_000) return u;
  }
  throw new Error('Fotografia demasiado detalhada');
}

export function Avatar({ name, color, photo, size = 40 }: { name: string; color?: string | null; photo?: string | null; size?: number }) {
  return photo
    ? <img src={photo} alt="" className="shrink-0 rounded-full object-cover" style={{ width: size, height: size }} />
    : <span aria-hidden className="grid shrink-0 place-items-center rounded-full font-bold text-white" style={{ width: size, height: size, background: color ?? '#64748b', fontSize: size * 0.4 }}>{name.slice(0, 1).toUpperCase()}</span>;
}

/** Equipa (docs/14): quem entra nas caixas com PIN e, com o módulo Profissionais, quem faz os serviços. */
export function OperatorsPage() {
  const { data, error, reload } = useLoad<{ items: Operator[] }>('/operators');
  const { has, type } = useBusiness();
  const [f, setF] = useState({ name: '', role: 'cashier', pin: '' });
  const [msg, setMsg] = useState<{ tone: 'error' | 'ok'; text: string } | null>(null);
  const [open, setOpen] = useState<Operator | null>(null);
  const [pinFor, setPinFor] = useState<Operator | null>(null);
  const staff = has('staff');
  if (error) return <Alert>{error}</Alert>;
  if (!data) return <Card><Skeleton rows={5} /></Card>;
  if (open) return <StaffSheet o={open} onClose={(changed) => { setOpen(null); if (changed) void reload(); }} />;
  const noun = type.terms.staff.toLowerCase();

  return (
    <div className="max-w-5xl space-y-6">
      {msg && <Alert tone={msg.tone}>{msg.text}</Alert>}
      <Card title={`Novo ${noun}`} description="Entra nas caixas com o PIN, que se escolhe aqui e nunca mais se mostra.">
        <form className="grid gap-4 sm:grid-cols-[1fr_180px_160px_auto] sm:items-end" onSubmit={async (e) => {
          e.preventDefault();
          const x = await attempt(() => post('/operators', f));
          setMsg(x ? { tone: 'error', text: x } : { tone: 'ok', text: `${f.name} pode entrar nas caixas com o PIN escolhido (depois da próxima sincronização).` });
          if (!x) { setF({ name: '', role: 'cashier', pin: '' }); void reload(); }
        }}>
          <Field label="Nome"><Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></Field>
          <Field label="Perfil"><Select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}><option value="cashier">Caixa</option><option value="manager">Gerente</option></Select></Field>
          <Field label="PIN" hint="4 a 6 algarismos"><Input required inputMode="numeric" pattern="\d{4,6}" maxLength={6} autoComplete="off" className="num tracking-widest" value={f.pin} onChange={(e) => setF({ ...f, pin: e.target.value.replace(/\D/g, '') })} /></Field>
          <Button icon={Plus}>Criar</Button>
        </form>
      </Card>
      <Card title={type.terms.staffPlural} padded={false}>
        {!data.items.length && <EmptyState icon={UserCog} title={`Ainda não há ${type.terms.staffPlural.toLowerCase()}`} text="Sem eles, as caixas não abrem." />}
        <ul className="divide-y divide-[var(--border)] p-2 xl:p-3">
          {data.items.map((o) => (
            <li key={o.id} className={clsx('flex flex-wrap items-center gap-3 rounded-lg px-2 py-2.5', !o.active && 'opacity-50')}>
              <Avatar name={o.name} color={o.color} photo={o.photo} />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5 font-medium">
                  {o.name} <Badge>{ROLE[o.role]}</Badge>
                  {staff && o.performs_services && <Badge tone="info">faz serviços</Badge>}
                  {staff && o.chair_rental && <Badge tone="warn">cadeira alugada</Badge>}
                </div>
                {staff && o.performs_services && (
                  <div className="text-xs text-muted">
                    {[Number(o.commission_service_pct) > 0 && `comissão ${Number(o.commission_service_pct)}% serviços`, Number(o.commission_product_pct) > 0 && `${Number(o.commission_product_pct)}% produtos`,
                      o.services.length ? `${o.services.length} serviços` : 'todos os serviços', o.schedule?.length ? `${new Set(o.schedule.map((s) => s.day)).size} dias por semana` : null].filter(Boolean).join(' · ')}
                  </div>
                )}
              </div>
              {staff && <Button variant="ghost" size="sm" onClick={() => setOpen(o)}>Ficha</Button>}
              <Button variant="ghost" size="sm" onClick={() => setPinFor(o)}>Mudar PIN</Button>
              <Button variant="ghost" size="sm" onClick={async () => {
                const x = await attempt(() => patch(`/operators/${o.id}`, { active: !o.active }));
                if (x) setMsg({ tone: 'error', text: x });
                void reload();
              }}>{o.active ? 'Desactivar' : 'Activar'}</Button>
            </li>
          ))}
        </ul>
      </Card>
      {pinFor && <PinDialog o={pinFor} onClose={(text) => { setPinFor(null); if (text) setMsg({ tone: 'ok', text }); }} />}
    </div>
  );
}

function PinDialog({ o, onClose }: { o: Operator; onClose: (msg?: string) => void }) {
  const [pin, setPin] = useState('');
  const [err, setErr] = useState<string | null>(null);
  return (
    <Dialog title={`Novo PIN — ${o.name}`} size="sm" onClose={() => onClose()} footer={<>
      <Button variant="ghost" onClick={() => onClose()}>Cancelar</Button>
      <Button disabled={!/^\d{4,6}$/.test(pin)} onClick={async () => {
        const x = await attempt(() => patch(`/operators/${o.id}`, { pin }));
        if (x) setErr(x); else onClose(`PIN de ${o.name} alterado.`);
      }}>Gravar</Button>
    </>}>
      <Field label="PIN" hint="4 a 6 algarismos" error={err}><Input autoFocus inputMode="numeric" maxLength={6} className="num tracking-widest" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))} /></Field>
    </Dialog>
  );
}

/** Ficha de profissional: identidade, comissões, horário e os serviços que faz (com preço e duração próprios). */
function StaffSheet({ o, onClose }: { o: Operator; onClose: (changed: boolean) => void }) {
  const products = useLoad<{ items: Product[] }>('/products');
  const toast = useToast();
  const file = useRef<HTMLInputElement>(null);
  const [f, setF] = useState({
    performsServices: o.performs_services, color: o.color ?? PALETTE[o.name.length % PALETTE.length]!, photo: o.photo, phone: o.phone ?? '',
    commissionServicePct: String(Number(o.commission_service_pct)), commissionProductPct: String(Number(o.commission_product_pct)), chairRental: o.chair_rental,
    schedule: o.schedule ?? [], allServices: !o.services.length,
    services: new Map(o.services.map((s) => [s.product_id, { price: s.price ? String(Number(s.price)) : '', durationMin: s.duration_min ? String(s.duration_min) : '' }])),
  });
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const up = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF({ ...f, [k]: v });
  const services = (products.data?.items ?? []).filter((p) => p.kind === 'service' && p.active);
  const slot = (day: number) => f.schedule.find((s) => s.day === day);
  const setSlot = (day: number, v: Slot | null) => up('schedule', [...f.schedule.filter((s) => s.day !== day), ...(v ? [v] : [])].sort((a, b) => a.day - b.day));

  const save = async () => {
    setBusy(true);
    const e = await attempt(() => patch(`/operators/${o.id}/staff`, {
      performsServices: f.performsServices, color: f.color, photo: f.photo, phone: f.phone || null,
      commissionServicePct: f.commissionServicePct || '0', commissionProductPct: f.commissionProductPct || '0', chairRental: f.chairRental, schedule: f.schedule,
      services: f.allServices ? [] : [...f.services.entries()].map(([productId, v]) => ({ productId, price: v.price.trim() || null, durationMin: v.durationMin ? Number(v.durationMin) : null })),
    }));
    setBusy(false); setErr(e);
    if (!e) { toast('Ficha gravada'); onClose(true); }
  };

  return (
    <div className="max-w-5xl">
      <Card title={o.name} description={`${ROLE[o.role]} · entra nas caixas com PIN`}>
        <div className="space-y-8">
          <Section title="Identificação" aside={
            <div className="flex items-center gap-3">
              <button type="button" onClick={() => file.current?.click()} aria-label="Fotografia" className="relative rounded-full ring-2 ring-[var(--border)] hover:ring-brand-500">
                <Avatar name={o.name} color={f.color} photo={f.photo} size={64} />
                <span className="absolute -bottom-1 -right-1 grid h-7 w-7 place-items-center rounded-full bg-ink text-white"><Camera size={14} /></span>
              </button>
              {f.photo && <Button variant="subtle" size="sm" onClick={() => up('photo', null)}>Tirar</Button>}
              <input ref={file} type="file" accept="image/*" hidden onChange={async (e) => {
                const x = e.target.files?.[0]; e.target.value = '';
                if (x) try { up('photo', await photoThumb(x)); } catch (ex) { setErr((ex as Error).message); }
              }} />
            </div>
          }>
            <Toggle checked={f.performsServices} onChange={(v) => up('performsServices', v)} label="Faz serviços" description="Aparece na fila, na agenda e na escolha de quem atendeu" />
            <Field label="Telefone"><PhoneInput value={f.phone} onChange={(v) => up('phone', v)} /></Field>
            <Field label="Cor" hint="Na agenda e na fila">
              <div className="flex flex-wrap gap-2">
                {PALETTE.map((c) => <button key={c} type="button" aria-label={`Cor ${c}`} aria-pressed={f.color === c} onClick={() => up('color', c)} className={clsx('h-9 w-9 rounded-full ring-offset-2 ring-offset-[var(--surface)]', f.color === c && 'ring-2 ring-[var(--text)]')} style={{ background: c }} />)}
              </div>
            </Field>
          </Section>

          {f.performsServices && <>
            <Section title="Comissões" description="Sobre o valor sem IVA, já com descontos. Relatório em Equipa → Comissões.">
              <Field label="Nos serviços"><NumberInput suffix="%" className="w-36" value={f.commissionServicePct} onChange={(e) => up('commissionServicePct', e.target.value)} /></Field>
              <Field label="Nos produtos que vende"><NumberInput suffix="%" className="w-36" value={f.commissionProductPct} onChange={(e) => up('commissionProductPct', e.target.value)} /></Field>
              <Toggle checked={f.chairRental} onChange={(v) => up('chairRental', v)} label="Aluga a cadeira" description="Profissional independente: as vendas dele ficam à parte e não levam comissão" />
            </Section>

            <Section title="Horário" description="Os dias e as horas em que atende (a agenda só marca dentro deles)." cols={1}>
              <div className="divide-y divide-[var(--border)] rounded-xl border border-base">
                {DAYS.map((d, i) => {
                  const s = slot(i);
                  return (
                    <div key={d} className="flex flex-wrap items-center gap-3 px-3 py-2">
                      <label className="flex w-36 items-center gap-2 text-sm font-medium">
                        <input type="checkbox" className="h-5 w-5 accent-[var(--color-brand-700)]" checked={!!s} onChange={(e) => setSlot(i, e.target.checked ? { day: i, from: '08:00', to: '17:00' } : null)} /> {d}
                      </label>
                      {s ? <>
                        <Input type="time" aria-label={`${d} — entrada`} className="w-32" value={s.from} onChange={(e) => setSlot(i, { ...s, from: e.target.value })} />
                        <span className="text-muted">às</span>
                        <Input type="time" aria-label={`${d} — saída`} className="w-32" value={s.to} onChange={(e) => setSlot(i, { ...s, to: e.target.value })} />
                      </> : <span className="text-sm text-muted">Folga</span>}
                    </div>
                  );
                })}
              </div>
            </Section>

            <Section title="Serviços que faz" description="O preço e a duração próprios substituem os do catálogo (ex.: o mestre cobra mais)." cols={1}>
              <Toggle checked={f.allServices} onChange={(v) => up('allServices', v)} label="Faz todos os serviços, aos preços do catálogo" />
              {!f.allServices && (
                services.length ? (
                  <div className="divide-y divide-[var(--border)] rounded-xl border border-base">
                    {services.map((p) => {
                      const v = f.services.get(p.id);
                      const set = (nv: typeof v) => { const m = new Map(f.services); if (nv) m.set(p.id, nv); else m.delete(p.id); up('services', m); };
                      return (
                        <div key={p.id} className="flex flex-wrap items-center gap-3 px-3 py-2">
                          <label className="flex min-w-48 flex-1 items-center gap-2 text-sm font-medium">
                            <input type="checkbox" className="h-5 w-5 accent-[var(--color-brand-700)]" checked={!!v} onChange={(e) => set(e.target.checked ? { price: '', durationMin: '' } : undefined)} />
                            {p.name} <span className="font-normal text-muted">· {formatMoney(p.unit_price)} MT{p.duration_min ? ` · ${p.duration_min} min` : ''}</span>
                          </label>
                          {v && <>
                            <NumberInput aria-label={`Preço de ${p.name}`} suffix="MT" placeholder={formatMoney(p.unit_price)} className="w-40" value={v.price} onChange={(e) => set({ ...v, price: e.target.value })} />
                            <NumberInput aria-label={`Duração de ${p.name}`} suffix="min" inputMode="numeric" placeholder={String(p.duration_min ?? '')} className="w-32" value={v.durationMin} onChange={(e) => set({ ...v, durationMin: e.target.value.replace(/\D/g, '') })} />
                          </>}
                        </div>
                      );
                    })}
                  </div>
                ) : <Alert tone="info">Ainda não há serviços no catálogo (artigos do tipo Serviço).</Alert>
              )}
            </Section>
          </>}
        </div>
        {err && <div className="mt-6"><Alert>{err}</Alert></div>}
        <FormActions>
          <Button onClick={save} disabled={busy}>{busy ? 'A gravar…' : 'Gravar'}</Button>
          <Button variant="ghost" onClick={() => onClose(false)}>Cancelar</Button>
        </FormActions>
      </Card>
    </div>
  );
}

interface CommissionRow { id: string; name: string; color: string | null; chair_rental: boolean; commission_service_pct: string; commission_product_pct: string; services_net: string; products_net: string; services_count: number; customers: number; commission_services: string; commission_products: string; commission: string }

/** Comissões por profissional no período (sobre o valor sem IVA, já com descontos; a devolução desconta). */
export function CommissionsPage() {
  const today = new Date().toISOString().slice(0, 10);
  const [p, setP] = useState({ from: `${today.slice(0, 8)}01`, to: today, training: false });
  const { data, error } = useLoad<{ items: CommissionRow[] }>(`/reports/commissions?from=${p.from}&to=${p.to}&training=${p.training ? 1 : 0}`);
  const total = (k: keyof CommissionRow) => (data?.items ?? []).filter((r) => !r.chair_rental).reduce((s, r) => s.plus(String(r[k])), dec(0));
  return (
    <div className="max-w-6xl space-y-6">
      <Card>
        <div className="flex flex-wrap items-end gap-3">
          <Field label="De"><Input type="date" className="w-45" value={p.from} onChange={(e) => setP({ ...p, from: e.target.value })} /></Field>
          <Field label="Até"><Input type="date" className="w-45" value={p.to} onChange={(e) => setP({ ...p, to: e.target.value })} /></Field>
          <label className="flex h-11 items-center gap-2 text-sm"><input type="checkbox" checked={p.training} onChange={(e) => setP({ ...p, training: e.target.checked })} /> Documentos de formação</label>
        </div>
      </Card>
      {error && <Alert>{error}</Alert>}
      {!data ? <Card><Skeleton rows={4} /></Card> : (
        <Card title="Comissões" description={`Total a pagar: ${formatMoney(total('commission').toFixed(2))} MT`} padded={false}>
          {!data.items.length ? <EmptyState icon={UserCog} title="Sem vendas com profissional neste período" text="Na caixa, cada linha leva quem fez o serviço (F6)." /> : (
            <div className="overflow-x-auto p-2 xl:p-3">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wide text-muted">
                  <tr><th className="px-2 py-2">Profissional</th><th className="px-2 text-right">Serviços</th><th className="hidden px-2 text-right md:table-cell">Base serviços</th><th className="hidden px-2 text-right md:table-cell">Base produtos</th><th className="px-2 text-right">Comissão</th></tr>
                </thead>
                <tbody className="divide-y divide-[var(--border)]">
                  {data.items.map((r) => (
                    <tr key={r.id}>
                      <td className="px-2 py-2.5"><div className="flex items-center gap-2.5"><Avatar name={r.name} color={r.color} size={32} /><div><div className="font-medium">{r.name}</div><div className="text-xs text-muted">{r.chair_rental ? 'Cadeira alugada (sem comissão)' : `${Number(r.commission_service_pct)}% serviços · ${Number(r.commission_product_pct)}% produtos`}</div></div></div></td>
                      <td className="num px-2 text-right">{r.services_count}<div className="text-xs text-muted">{r.customers} clientes</div></td>
                      <td className="num hidden px-2 text-right md:table-cell">{formatMoney(r.services_net)}</td>
                      <td className="num hidden px-2 text-right md:table-cell">{formatMoney(r.products_net)}</td>
                      <td className="num px-2 text-right font-bold">{r.chair_rental ? '—' : `${formatMoney(r.commission)} MT`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

